import { motion } from "framer-motion";
import { Ship, Compass, ShieldCheck, Landmark, Cpu } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = [Ship, Compass, ShieldCheck, Landmark, Cpu];
const SPANS = [
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-2",
  "lg:col-span-2",
  "lg:col-span-2",
];

export default function Services({ t }) {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-forest py-24 text-cream sm:py-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-gold/5 blur-[100px]"
      />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-gold"
        >
          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
          {t.services.eyebrow}
        </motion.p>

        <div className="mb-16 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-serif text-2xl font-normal leading-tight sm:text-3xl lg:text-4xl"
          >
            {t.services.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="max-w-md text-sm leading-relaxed text-cream/60 sm:text-base"
          >
            {t.services.sub}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.09, ease: EASE }}
                whileHover={{ y: -6 }}
                data-testid={`service-card-0${i + 1}`}
                className={`group relative overflow-hidden rounded-sm border border-cream/10 bg-carddark/80 p-7 transition-colors duration-500 hover:border-gold/60 ${SPANS[i]}`}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gold/0 blur-3xl transition-all duration-700 group-hover:bg-gold/15"
                />
                <div className="flex items-start justify-between">
                  <Icon
                    className="h-6 w-6 text-gold transition-transform duration-500 group-hover:scale-110"
                    strokeWidth={1.5}
                  />
                  <span className="font-mono text-[11px] tracking-[0.2em] text-cream/30">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-14 font-serif text-xl font-medium leading-snug text-cream sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/55">
                  {s.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
